// src/components/JumpLayoutSettings.tsx
/**
 * Phase 2: Jump Indicator Layout Fix - Settings Panel
 * 
 * UI component for configuring jump layout behavior:
 * - Chain sequential jumps toggle
 * - Layout preferences
 * 
 * Based on DNL Enhancement Specification v1.0, Section 3.3
 */

import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link2, Unlink2, Info, Settings2, RotateCcw, Plus, X, AlertTriangle } from 'lucide-react';
import type { RootState } from '../types/redux';
import {
  toggleChainSequentialJumps,
  setChainSequentialJumps,
  updateLayoutSettings,
  resetLayoutSettings,
} from '../store/slices/jumpIndicatorsSlice';

// =============================================================================
// TYPES
// =============================================================================

export interface JumpLayoutSettingsProps {
  /** Compact display mode */
  compact?: boolean;
  /** Dark mode */
  isDark?: boolean;
  /** Whether to show as a collapsible section */
  collapsible?: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const JumpLayoutSettings: React.FC<JumpLayoutSettingsProps> = ({
  compact = false,
  isDark = false,
  collapsible = false,
}) => {
  const dispatch = useDispatch();
  const layoutSettings = useSelector((state: RootState) => state.jumpIndicators.layoutSettings);
  const [isExpanded, setIsExpanded] = React.useState(!collapsible);
  
  const handleToggleChain = () => {
    dispatch(toggleChainSequentialJumps());
  };
  
  const handleArcHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 10 && value <= 100) {
      dispatch(updateLayoutSettings({ defaultArcHeight: value }));
    }
  };
  
  const handleLabelPositionChange = (position: 'inside' | 'above') => {
    dispatch(updateLayoutSettings({ labelPosition: position }));
  };
  
  const handleReset = () => {
    dispatch(resetLayoutSettings());
  };

  const handleJumpTypeChange = (type: 'additive' | 'multiplicative') => {
    dispatch(updateLayoutSettings({ jumpType: type }));
  };

  const handleScaleFactorFormatChange = (format: 'SF=' | '×') => {
    dispatch(updateLayoutSettings({ scaleFactorFormat: format }));
  };

  const handleShowCalculationChange = () => {
    dispatch(updateLayoutSettings({ showCalculation: !layoutSettings.showCalculation }));
  };

  const handleShowResultChange = () => {
    dispatch(updateLayoutSettings({ showResult: !layoutSettings.showResult }));
  };

  const handleAlwaysShowSignsChange = () => {
    dispatch(updateLayoutSettings({ alwaysShowSigns: !layoutSettings.alwaysShowSigns }));
  };

  const handleShowJumpValueChange = () => {
    dispatch(updateLayoutSettings({ showJumpValue: !layoutSettings.showJumpValue }));
  };
  
  const textColor = isDark ? 'text-gray-200' : 'text-gray-800';
  const mutedColor = isDark ? 'text-gray-400' : 'text-gray-600';
  const bgColor = isDark ? 'bg-gray-800' : 'bg-gray-50';
  const borderColor = isDark ? 'border-gray-700' : 'border-gray-200';
  
  // Check if any jumps start from zero (multiplicative mode won't work for those)
  const allJumps = useSelector((state: RootState) => state.jumpIndicators.jumps);
  const hasZeroStartJumps = allJumps.some(j => Math.abs(j.startValue) < 1e-10);
  
  return (
    <div className={`jump-layout-settings ${bgColor} rounded-lg border ${borderColor} p-3`}>
      {/* Header */}
      {collapsible ? (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`w-full flex items-center justify-between ${textColor} hover:opacity-80`}
        >
          <div className="flex items-center gap-2">
            <Settings2 size={16} />
            <span className="font-medium text-sm">Jump Layout Settings</span>
          </div>
          <span className="text-xs">{isExpanded ? '▼' : '▶'}</span>
        </button>
      ) : (
        <div className={`flex items-center gap-2 ${textColor} mb-3`}>
          <Settings2 size={16} />
          <span className="font-medium text-sm">Jump Layout Settings</span>
        </div>
      )}
      
      {/* Content */}
      {isExpanded && (
        <div className={`space-y-4 ${collapsible ? 'mt-3' : ''}`}>
          {/* Chain Sequential Jumps Toggle */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className={`flex items-center gap-2 text-sm ${textColor}`}>
                {layoutSettings.chainSequentialJumps ? (
                  <Link2 size={16} className="text-violet-500" />
                ) : (
                  <Unlink2 size={16} className={mutedColor} />
                )}
                <span>Chain sequential jumps</span>
              </label>
              
              <button
                onClick={handleToggleChain}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  layoutSettings.chainSequentialJumps 
                    ? 'bg-violet-500' 
                    : isDark ? 'bg-gray-600' : 'bg-gray-300'
                }`}
                aria-pressed={layoutSettings.chainSequentialJumps}
                role="switch"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    layoutSettings.chainSequentialJumps ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            
            {/* Explanation tooltip */}
            <div className={`flex items-start gap-2 text-xs ${mutedColor} pl-6`}>
              <Info size={12} className="mt-0.5 flex-shrink-0" />
              <p>
                {layoutSettings.chainSequentialJumps
                  ? 'Sequential jumps (0→3, 3→6) will appear side-by-side at the same height, ideal for "rated addition" reasoning.'
                  : 'All jumps will stack vertically regardless of whether they form a sequence.'}
              </p>
            </div>
            
            {/* Visual illustration */}
            <div className={`${isDark ? 'bg-gray-900' : 'bg-white'} rounded p-2 border ${borderColor}`}>
              <div className="text-xs font-mono text-center">
                {layoutSettings.chainSequentialJumps ? (
                  <pre className={mutedColor}>
{`  ╭───╮╭─────╮
  │×3 ││ ×2  │
──┴───┴┴─────┴──
  0   3      6`}
                  </pre>
                ) : (
                  <pre className={mutedColor}>
{`     ╭───────╮
  ╭──┤  ×2   │
  │×3╰───────╯
──┴──────────────
  0   3      6`}
                  </pre>
                )}
              </div>
              <div className={`text-center text-xs ${mutedColor} mt-1`}>
                {layoutSettings.chainSequentialJumps ? 'Side-by-side (chains)' : 'Stacked (traditional)'}
              </div>
            </div>
          </div>

          {/* Jump Display Options */}
          <div className="space-y-4">
            <div className={`flex items-center gap-2 ${textColor} mb-2`}>
              <Settings2 size={16} />
              <span className="font-medium text-sm">Jump Display Options</span>
            </div>

            {/* Jump Type */}
            <div className="space-y-2">
              <label className={`text-sm ${textColor}`}>Jump relationship type</label>
              <div className="flex gap-2">
                <button
                  onClick={() => handleJumpTypeChange('additive')}
                  className={`flex-1 px-3 py-2 text-xs rounded border flex items-center justify-center gap-2 ${
                    layoutSettings.jumpType === 'additive'
                      ? 'bg-violet-500 text-white border-violet-500'
                      : `${borderColor} ${textColor} hover:bg-opacity-50`
                  }`}
                >
                  <Plus size={14} />
                  Additive
                </button>
                <button
                  onClick={() => handleJumpTypeChange('multiplicative')}
                  className={`flex-1 px-3 py-2 text-xs rounded border flex items-center justify-center gap-2 ${
                    layoutSettings.jumpType === 'multiplicative'
                      ? 'bg-violet-500 text-white border-violet-500'
                      : `${borderColor} ${textColor} hover:bg-opacity-50`
                  }`}
                >
                  <X size={14} />
                  Multiplicative
                </button>
              </div>
              {layoutSettings.jumpType === 'multiplicative' && hasZeroStartJumps && (
                <div className={`flex items-start gap-2 text-xs ${isDark ? 'text-yellow-400' : 'text-yellow-600'} bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded border ${borderColor}`}>
                  <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                  <p>Some jumps start from zero. Multiplicative mode only works for non-zero start values. Those jumps will display in additive mode.</p>
                </div>
              )}
              <div className={`flex items-start gap-2 text-xs ${mutedColor} pl-2`}>
                <Info size={12} className="mt-0.5 flex-shrink-0" />
                <p>
                  {layoutSettings.jumpType === 'additive'
                    ? 'Show jumps as addition: start + span = end'
                    : 'Show jumps as multiplication: start × factor = end (only works for non-zero start values)'}
                </p>
              </div>
            </div>

            {/* Scale Factor Format (only shown in multiplicative mode) */}
            {layoutSettings.jumpType === 'multiplicative' && (
              <div className="space-y-2">
                <label className={`text-sm ${textColor}`}>Scale factor format</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleScaleFactorFormatChange('SF=')}
                    className={`flex-1 px-3 py-2 text-xs rounded border ${
                      layoutSettings.scaleFactorFormat === 'SF='
                        ? 'bg-violet-500 text-white border-violet-500'
                        : `${borderColor} ${textColor} hover:bg-opacity-50`
                    }`}
                  >
                    SF=
                  </button>
                  <button
                    onClick={() => handleScaleFactorFormatChange('×')}
                    className={`flex-1 px-3 py-2 text-xs rounded border ${
                      layoutSettings.scaleFactorFormat === '×'
                        ? 'bg-violet-500 text-white border-violet-500'
                        : `${borderColor} ${textColor} hover:bg-opacity-50`
                    }`}
                  >
                    ×
                  </button>
                </div>
                <div className={`flex items-start gap-2 text-xs ${mutedColor} pl-2`}>
                  <Info size={12} className="mt-0.5 flex-shrink-0" />
                  <p>Choose how to display the scale factor: "SF=3" or "×3"</p>
                </div>
              </div>
            )}

            {/* Show Calculation Toggle */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className={`text-sm ${textColor}`}>Show calculation</label>
                <button
                  onClick={handleShowCalculationChange}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    layoutSettings.showCalculation 
                      ? 'bg-violet-500' 
                      : isDark ? 'bg-gray-600' : 'bg-gray-300'
                  }`}
                  aria-pressed={layoutSettings.showCalculation}
                  role="switch"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      layoutSettings.showCalculation ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <div className={`flex items-start gap-2 text-xs ${mutedColor} pl-2`}>
                <Info size={12} className="mt-0.5 flex-shrink-0" />
                <p>
                  {layoutSettings.jumpType === 'additive'
                    ? 'Show the calculation (e.g., "0+3" or "+3")'
                    : 'Show the calculation (e.g., "1×3" or "×3" or "SF=3")'}
                </p>
              </div>
            </div>

            {/* Show Result Toggle */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className={`text-sm ${textColor}`}>Show result</label>
                <button
                  onClick={handleShowResultChange}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    layoutSettings.showResult 
                      ? 'bg-violet-500' 
                      : isDark ? 'bg-gray-600' : 'bg-gray-300'
                  }`}
                  aria-pressed={layoutSettings.showResult}
                  role="switch"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      layoutSettings.showResult ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <div className={`flex items-start gap-2 text-xs ${mutedColor} pl-2`}>
                <Info size={12} className="mt-0.5 flex-shrink-0" />
                <p>Show the result value (e.g., "3" or "=3")</p>
              </div>
            </div>

            {/* Show Jump Value Toggle */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className={`text-sm ${textColor}`}>Show jump value</label>
                <button
                  onClick={handleShowJumpValueChange}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    layoutSettings.showJumpValue 
                      ? 'bg-violet-500' 
                      : isDark ? 'bg-gray-600' : 'bg-gray-300'
                  }`}
                  aria-pressed={layoutSettings.showJumpValue}
                  role="switch"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      layoutSettings.showJumpValue ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <div className={`flex items-start gap-2 text-xs ${mutedColor} pl-2`}>
                <Info size={12} className="mt-0.5 flex-shrink-0" />
                <p>
                  {layoutSettings.jumpType === 'additive'
                    ? 'Show the jump span value (e.g., "+3" or "-2")'
                    : layoutSettings.scaleFactorFormat === 'SF='
                    ? 'Show the scale factor (e.g., "SF=2" or "SF=4")'
                    : 'Show the scale factor (e.g., "×2" or "×4")'}
                </p>
              </div>
            </div>

            {/* Always Show Signs Toggle */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className={`text-sm ${textColor}`}>Always show signs</label>
                <button
                  onClick={handleAlwaysShowSignsChange}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    layoutSettings.alwaysShowSigns 
                      ? 'bg-violet-500' 
                      : isDark ? 'bg-gray-600' : 'bg-gray-300'
                  }`}
                  aria-pressed={layoutSettings.alwaysShowSigns}
                  role="switch"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      layoutSettings.alwaysShowSigns ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <div className={`flex items-start gap-2 text-xs ${mutedColor} pl-2`}>
                <Info size={12} className="mt-0.5 flex-shrink-0" />
                <p>Always include + or - signs on numbers (e.g., "+3" instead of "3")</p>
              </div>
            </div>
          </div>
          
          {/* Advanced Settings (non-compact mode) */}
          {!compact && (
            <>
              <hr className={borderColor} />
              
              {/* Arc Height */}
              <div className="space-y-1">
                <label className={`text-sm ${textColor}`}>
                  Default arc height: {layoutSettings.defaultArcHeight}px
                </label>
                <input
                  type="range"
                  min={10}
                  max={60}
                  value={layoutSettings.defaultArcHeight}
                  onChange={handleArcHeightChange}
                  className="w-full"
                />
              </div>
              
              {/* Label Position */}
              <div className="space-y-1">
                <label className={`text-sm ${textColor}`}>Label position</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleLabelPositionChange('above')}
                    className={`px-3 py-1 text-xs rounded border ${
                      layoutSettings.labelPosition === 'above'
                        ? 'bg-violet-500 text-white border-violet-500'
                        : `${borderColor} ${textColor}`
                    }`}
                  >
                    Above arc
                  </button>
                  <button
                    onClick={() => handleLabelPositionChange('inside')}
                    className={`px-3 py-1 text-xs rounded border ${
                      layoutSettings.labelPosition === 'inside'
                        ? 'bg-violet-500 text-white border-violet-500'
                        : `${borderColor} ${textColor}`
                    }`}
                  >
                    Inside arc
                  </button>
                </div>
              </div>
              
              {/* Reset Button */}
              <div className="pt-2">
                <button
                  onClick={handleReset}
                  className={`flex items-center gap-1 text-xs ${mutedColor} hover:text-red-500 transition-colors`}
                >
                  <RotateCcw size={12} />
                  Reset to defaults
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// COMPACT TOGGLE
// =============================================================================

/**
 * Minimal toggle-only version for toolbar use
 */
export const ChainJumpsToggle: React.FC<{ isDark?: boolean }> = ({ isDark = false }) => {
  const dispatch = useDispatch();
  const chainEnabled = useSelector(
    (state: RootState) => state.jumpIndicators.layoutSettings.chainSequentialJumps
  );
  
  return (
    <button
      onClick={() => dispatch(toggleChainSequentialJumps())}
      className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
        chainEnabled
          ? 'bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300'
          : isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'
      }`}
      title={chainEnabled ? 'Chain jumps horizontally (ON)' : 'Chain jumps horizontally (OFF)'}
    >
      {chainEnabled ? <Link2 size={14} /> : <Unlink2 size={14} />}
      <span className="hidden sm:inline">Chain</span>
    </button>
  );
};

export default JumpLayoutSettings;
